import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Briefcase, Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email.trim()) {
            toast.error('Please enter your email address');
            return;
        }
        
        try {
            setLoading(true);
            await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim() });
            setSubmitted(true);
            toast.success('Check your email for the reset link');
        } catch (error) {
            console.error('Forgot password error:', error);
            // Still show success to prevent email enumeration
            setSubmitted(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F5EDE5] to-[#E8D5C4] flex items-center justify-center p-4">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4BBA6]/30 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#E8D5C4]/50 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#4A3728] mb-4 shadow-lg">
                        <Briefcase className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-[#4A3728] uppercase tracking-wide">
                        SEVORA TEAM
                    </h1>
                </div>

                <Card className="bg-white/95 backdrop-blur-xl border-[#D4BBA6] shadow-xl">
                    <CardHeader className="text-center pb-4">
                        <CardTitle className="text-[#4A3728] text-xl">
                            {submitted ? 'Check Your Email' : 'Forgot Password'}
                        </CardTitle>
                        <CardDescription className="text-[#5D4A3A]">
                            {submitted 
                                ? 'We sent you instructions to reset your password'
                                : 'Enter your email to receive a password reset link'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {submitted ? (
                            <div className="text-center py-4">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-green-600" />
                                </div>
                                <p className="text-[#5D4A3A] text-sm mb-6">
                                    If an account exists with <strong>{email}</strong>, you will receive 
                                    a password reset link within a few minutes.
                                </p>
                                <p className="text-[#9C8C74] text-xs mb-6">
                                    Don't see it? Check your spam folder or try again.
                                </p>
                                <div className="space-y-3">
                                    <Button 
                                        variant="outline"
                                        onClick={() => { setSubmitted(false); setEmail(''); }}
                                        className="w-full border-[#D4BBA6] text-[#4A3728] hover:bg-[#F5EDE5]"
                                    >
                                        Try another email
                                    </Button>
                                    <Link to="/login">
                                        <Button 
                                            className="w-full bg-[#4A3728] hover:bg-[#3A2A1E] text-white"
                                        >
                                            Back to Login
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-[#4A3728]">Email Address</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5D4A3A]" />
                                        <Input
                                            type="email"
                                            placeholder="you@company.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="pl-10 bg-white border-[#D4BBA6] text-[#4A3728] placeholder:text-[#5D4A3A]/50 focus:border-[#4A3728] focus:ring-[#4A3728]"
                                            required
                                            data-testid="forgot-email-input"
                                        />
                                    </div>
                                </div>
                                <Button 
                                    type="submit" 
                                    disabled={loading}
                                    className="w-full bg-[#4A3728] hover:bg-[#3A2A1E] text-white shadow-md"
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Send Reset Link'
                                    )}
                                </Button>
                                
                                <Link 
                                    to="/login" 
                                    className="flex items-center justify-center gap-2 text-sm text-[#5D4A3A] hover:text-[#4A3728] transition-colors mt-4"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to Login
                                </Link>
                            </form>
                        )}
                    </CardContent>
                </Card>

                <p className="text-center text-xs text-[#5D4A3A] mt-6">
                    Need help? Contact your administrator
                </p>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
